import type { ElementParameterBatchUpdate } from '../api/revit/revitBridge';
import type { RevitOperationProgressSnapshot } from '../api/grpc/operationProgressClient';

export interface PropertyMapping {
  label: string;
  revitParameter: string;
  bindingScope?: QuickFillBindingScope;
}

export interface CellUpdateResult {
  familyType: string;
  parameter: string;
  value: string;
  success: boolean;
  error?: string;
}

export interface BulkUpdateResult {
  totalUpdates: number;
  successCount: number;
  failureCount: number;
  results: CellUpdateResult[];
  /** Authoritative model version reported by the host after the apply committed. */
  modelVersion?: number;
}

export type QuickFillSyncProgressPhase =
  | 'preparing'
  | 'writing-project-types'
  | 'writing-elements'
  | 'completed';

export interface QuickFillSyncProgress {
  operationId?: string;
  phase: QuickFillSyncProgressPhase;
  message: string;
  current: number;
  total: number;
  detail?: string;
  functionName?: string;
  currentItem?: string;
  state?: RevitOperationProgressSnapshot['state'];
  waitReason?: string;
  waitElapsedMs?: number;
  activeElapsedMs?: number;
  percent?: number | null;
  elapsedMs?: number;
  etaMs?: number | null;
  cacheStatus?: string;
  resultReady?: boolean;
  failed?: boolean;
  error?: string;
  items?: RevitOperationProgressSnapshot['items'];
}

export interface QuickFillCellUpdate {
  rowIndex: number;
  field: string;
  value: string;
  revitParameter: string;
  bindingScope?: QuickFillBindingScope;
}

export type QuickFillBindingScope = 'type' | 'instance';

export interface PreparedProjectTypeUpdate {
  FamilyType: string;
  ParameterName: string;
  Value: string;
  FamilyTypeId?: string;
  TargetElementIds?: string[];
  BindingScope: QuickFillBindingScope | 'unknown';
  rowLabel: string;
}

export interface PreparedElementUpdate extends ElementParameterBatchUpdate {
  rowLabel: string;
}

export interface CollectedQuickFillUpdates {
  projectTypeUpdates: PreparedProjectTypeUpdate[];
  elementUpdates: PreparedElementUpdate[];
  skippedResults: CellUpdateResult[];
}

export interface SaveChangesOptions {
  includeEmptyValues?: boolean;
  onProgress?: (progress: QuickFillSyncProgress) => void;
}

export function emitProgress(
  options: SaveChangesOptions | undefined,
  progress: QuickFillSyncProgress
): void {
  options?.onProgress?.(progress);
}

export function emitPreparingProgress(
  options: SaveChangesOptions | undefined,
  detail?: string
): void {
  emitProgress(options, {
    phase: 'preparing',
    message: 'Preparing Quick Fill changes for Revit',
    current: 0,
    total: 0,
    detail,
  });
}
